import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { language } = useLanguage();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error(language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères'
        : 'Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(language === 'fr' 
        ? 'Les mots de passe ne correspondent pas'
        : 'Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await api.post('/auth/change-password', {
        new_password: newPassword
      });
      
      toast.success(language === 'fr' 
        ? 'Mot de passe changé avec succès!'
        : 'Password changed successfully!');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      // Redirect to dashboard or onboarding
      navigate('/dashboard');
    } catch (err) {
      console.error('Error changing password:', err);
      const message = err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
            Space<span className="text-primary">LeadX</span>
          </h1>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto p-3 bg-primary/10 rounded-full w-fit mb-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              {language === 'fr' 
                ? 'Changez votre mot de passe'
                : 'Change Your Password'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Pour des raisons de sécurité, vous devez créer un nouveau mot de passe.'
                : 'For security reasons, you must create a new password.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">
                  {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="pr-10"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Minimum 8 caractères'
                    : 'Minimum 8 characters'}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">
                  {language === 'fr' ? 'Confirmez le mot de passe' : 'Confirm Password'}
                </Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 font-bold"
                disabled={isLoading}
                data-testid="change-password-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {language === 'fr' ? 'Continuer' : 'Continue'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
